// === Acting-as-company banner ===
// Strip shown at the top of every page while a superadmin is working inside a
// customer's company. Renders nothing for normal users.
//
// The signal is `actingCompanyName` from the auth context, which comes from the
// SERVER (the active "view as" row) — not from client storage. That matters:
// the banner can never disagree with the scope the API and RLS are actually
// applying, and there is no per-tab marker to leak or go stale.
//
// Exit is a server call: /impersonation/stop deletes the row, so the next
// request — API or direct Supabase query — resolves to no company and the
// superadmin lands back on the picker. The superadmin stays signed in as
// themselves throughout; there is no borrowed session to tear down.

import { useCallback, useState } from 'react';
import type { JSX } from 'react';
import { Alert, Button, Box, CircularProgress, Stack } from '@mui/material';
import { Logout as ExitIcon, SwapHoriz as SwitchIcon, AdminPanelSettings as ShieldIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';

export function ImpersonationBanner(): JSX.Element | null {
  const { isSuperadmin, actingCompanyName, companyId, exitCompany } = useAuth();
  const navigate = useNavigate();
  const [exiting, setExiting] = useState(false);

  const handleExit = useCallback((): void => {
    setExiting(true);
    void (async () => {
      await exitCompany();
      navigate('/select-company', { replace: true });
    })();
  }, [exitCompany, navigate]);

  if (!isSuperadmin || !companyId) return null;

  return (
    <Box sx={{ position: 'sticky', top: 0, zIndex: 1200 }}>
      <Alert
        severity="warning"
        icon={<ShieldIcon fontSize="inherit" />}
        sx={{ borderRadius: 0, alignItems: 'center', fontWeight: 500 }}
        action={
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={<SwitchIcon />}
              onClick={() => { navigate('/select-company'); }}
              disabled={exiting}
            >
              Switch company
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={exiting ? <CircularProgress size={14} color="inherit" /> : <ExitIcon />}
              onClick={handleExit}
              disabled={exiting}
            >
              {exiting ? 'Exiting…' : 'Exit company'}
            </Button>
          </Stack>
        }
      >
        Superadmin: working in <strong>{actingCompanyName ?? 'this company'}</strong>. Changes apply
        to their data and are recorded against your account.
      </Alert>
    </Box>
  );
}
